/* =========================================================================
   BONCAP — Klaviyo capture
   Every email form on this theme posts here, and nowhere else. Shopify's
   customer form is deliberately not used: it writes a Shopify customer, which
   reaches Klaviyo as a consent flag rather than a list join, so list-triggered
   flows do not fire reliably.

   Endpoint: Klaviyo's CLIENT API, which is designed to be called from a
   browser with the PUBLIC key. No private key is ever present on the
   storefront. A 202 means accepted.

   Config comes from theme settings (Theme settings -> Klaviyo), printed into
   window.BoncapKlaviyo by layout/theme.liquid, so nothing here is hardcoded.
   ===================================================================== */
(function () {
  'use strict';

  var CFG = window.BoncapKlaviyo || {};
  if (!CFG.publicKey) return;

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/?company_id=' +
                 encodeURIComponent(CFG.publicKey);

  /* Attribution. First touch is stored once per browser on the first page
     view (UTMs, referrer, landing page), so a shopper who lands from a TikTok
     ad, browses, then signs up three pages later still carries the ad's
     UTMs. Last-touch UTMs on the signup page itself are sent too. Storage is
     wrapped because private mode and blocked site data throw. */
  var ATTR_KEY = 'boncap_first_touch';
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  function readUtms() {
    var out = {};
    try {
      var qs = new URLSearchParams(window.location.search);
      UTM_KEYS.forEach(function (k) { if (qs.get(k)) out[k] = qs.get(k); });
      if (qs.get('ttclid')) out.click_id = 'tiktok';
      else if (qs.get('fbclid')) out.click_id = 'meta';
      else if (qs.get('gclid')) out.click_id = 'google';
    } catch (e) { /* old browser, no URLSearchParams */ }
    return out;
  }

  (function storeFirstTouch() {
    try {
      if (window.localStorage.getItem(ATTR_KEY)) return;
      var ref = document.referrer && document.referrer.indexOf(window.location.hostname) === -1 ? document.referrer : '';
      var first = readUtms();
      first.referrer = ref || 'direct';
      first.landing_page = window.location.pathname;
      first.at = new Date().toISOString();
      window.localStorage.setItem(ATTR_KEY, JSON.stringify(first));
    } catch (e) { /* storage blocked */ }
  })();

  function attribution() {
    var props = {};
    var first = {};
    try { first = JSON.parse(window.localStorage.getItem(ATTR_KEY) || '{}'); } catch (e) { first = {}; }
    Object.keys(first).forEach(function (k) {
      if (k !== 'at') props['first_' + k] = first[k];
    });
    var last = readUtms();
    Object.keys(last).forEach(function (k) { props['signup_' + k] = last[k]; });
    props.signup_page = window.location.pathname;
    props.signup_device = window.matchMedia && window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
    return props;
  }

  function say(form, text, ok) {
    var msg = form.querySelector('.klaviyo-form__msg');
    if (!msg) return;
    msg.textContent = text;
    msg.hidden = false;
    msg.dataset.ok = ok ? 'true' : 'false';
  }

  /* Normalises a US phone number to E.164 (+1XXXXXXXXXX). Returns null for
     anything that is clearly not a 10-digit US number, so the caller can
     give a specific message instead of sending garbage to Klaviyo. */
  function normalizeUSPhone(raw) {
    var digits = (raw || '').replace(/\D/g, '');
    if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
    if (digits.length !== 10) return null;
    return '+1' + digits;
  }

  /* Klaviyo's error body is JSON:API — { errors: [{ detail, ... }] }. Falls
     back to the theme-wide error message when the body is not JSON or has
     no errors array, so a network failure never shows nothing. */
  function describeError(res) {
    return res.text().then(function (body) {
      try {
        var parsed = JSON.parse(body);
        var first = parsed && parsed.errors && parsed.errors[0];
        if (first && first.detail) return first.detail;
      } catch (e) { /* not JSON, fall through */ }
      return CFG.errorText;
    }).catch(function () { return CFG.errorText; });
  }

  function subscribe(form) {
    var input = form.querySelector('input[type="email"]');
    /* Optional phone field, used by the BFCM waitlist form and any future
       one. Its presence, not a separate checkbox, is what turns SMS consent
       on — no phone typed means no SMS subscription object goes out. */
    var phoneInput = form.querySelector('input[type="tel"]');
    /* Step two of the waitlist carries the number step one already captured
       in a hidden field (not type="tel", so it never counts as a fresh
       phone field needing its own SMS consent line). */
    var capturedPhoneInput = form.querySelector('[data-captured-phone]');
    var btn = form.querySelector('button[type="submit"]');
    var wantsEmail = !!input;
    var wantsPhone = !!phoneInput;
    var email = (input && input.value || '').trim();
    var phoneRaw = (phoneInput && phoneInput.value || '').trim();
    var listId = form.dataset.list;

    if (wantsEmail && (!email || email.indexOf('@') < 1)) {
      say(form, 'That email does not look right.', false);
      if (input) input.focus();
      return;
    }

    var phone = null;
    if (wantsPhone) {
      phone = normalizeUSPhone(phoneRaw);
      if (!phone) {
        say(form, 'That phone number does not look right. Use a 10-digit US number.', false);
        if (phoneInput) phoneInput.focus();
        return;
      }
    } else if (capturedPhoneInput && capturedPhoneInput.value) {
      /* best-effort carry-forward; a malformed captured value just means no
         phone_number on this call, never a hard error on the email step */
      phone = normalizeUSPhone(capturedPhoneInput.value);
    }

    if (!listId) { say(form, CFG.errorText, false); return; }

    if (btn) { btn.disabled = true; btn.dataset.was = btn.textContent; btn.textContent = '…'; }

    /* data-properties carries any per-form custom properties as a JSON
       string (e.g. waitlist_product), merged over the standard source tag
       so one profile field never has to be renamed to fit two use cases. */
    var props = attribution();
    props.signup_source = form.dataset.source || 'theme';
    if (form.dataset.properties) {
      try {
        var extra = JSON.parse(form.dataset.properties);
        for (var k in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, k)) props[k] = extra[k];
        }
      } catch (e) { /* malformed data-properties is ignored, not fatal */ }
    }

    var profileAttrs = { properties: props, subscriptions: {} };
    if (email) profileAttrs.email = email;
    if (phone) profileAttrs.phone_number = phone;
    if (wantsEmail && email) {
      profileAttrs.subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
    }
    if (phone && (wantsPhone || form.dataset.step === 'phone')) {
      profileAttrs.subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
    }

    var payload = {
      data: {
        type: 'subscription',
        attributes: { profile: { data: { type: 'profile', attributes: profileAttrs } } },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };

    fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'revision': CFG.revision || '2025-01-15'
      },
      body: JSON.stringify(payload)
    }).then(function (res) {
      /* Klaviyo answers 202 Accepted with no body on success. */
      if (res.status === 202 || res.ok) {
        form.reset();
        /* data-success lets a form (the waitlist one) show its own copy
           instead of the theme-wide default. data-step distinguishes the
           waitlist's two-step form: "phone" (step one) fires its own event
           so the form can advance to step two instead of showing the final
           done state, which only step two (or a single-step form) reaches. */
        say(form, form.dataset.success || CFG.successText, true);
        if (form.dataset.step === 'phone') {
          form.dispatchEvent(new CustomEvent('boncap:klaviyo:phone-done', { bubbles: true, detail: { phone: phone } }));
        } else {
          form.dispatchEvent(new CustomEvent('boncap:klaviyo:subscribed', { bubbles: true }));
        }
        return null;
      }
      return describeError(res).then(function (detail) {
        say(form, detail, false);
      });
    }).catch(function () {
      say(form, CFG.errorText, false);
    }).then(function () {
      if (btn) { btn.disabled = false; btn.textContent = btn.dataset.was || 'Submit'; }
    });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('[data-klaviyo-form]')) return;
    e.preventDefault();
    subscribe(form);
  });
})();
